let activeProfile = localStorage.getItem('ig-profile') || 'alex';
let csrfToken = '';
type AccountTransport = (path: string, method: string, body?: unknown) => Promise<unknown>;
let accountTransport: AccountTransport | undefined;
export function setAccountTransport(transport: AccountTransport) {
  accountTransport = transport;
}
export const setCsrfToken = (value: string | null | undefined) => {
  csrfToken = value || '';
};
export const getProfileId = () => activeProfile;
export function setProfileId(id: string, email?: string) {
  activeProfile = id;
  localStorage.setItem('ig-profile', id);
  window.dispatchEvent(new CustomEvent('ig-profile-selected', { detail: { id, email } }));
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
  }
}
type PasteObservation = {
  requestId: string;
  text: string;
  origin: string;
  destination: string;
  [key: string]: unknown;
};
const pasteQueues = new Map<string, PasteObservation[]>();
const pasteSyncs = new Map<string, Promise<unknown>>();
export function clearAccountDeviceData(profile: string) {
  for (const key of Object.keys(sessionStorage))
    if (key.startsWith(`ig-assignment-feedback:${profile}:`)) sessionStorage.removeItem(key);
  const prefixes = [`ig-pastes:${profile}:`, `ig-work:${profile}:`, `ig-time:${profile}:`, `ig-input-pastes:${profile}`, `ig-voice:${profile}:`];
  for (const key of Object.keys(localStorage))
    if (prefixes.some((prefix) => key.startsWith(prefix)) || key.includes(`|${profile}|`))
      localStorage.removeItem(key);
  for (const [key, queue] of pasteQueues)
    if (prefixes.some((prefix) => key.startsWith(prefix))) {
      queue.length = 0;
      pasteQueues.delete(key);
    }
  if (activeProfile === profile) {
    localStorage.removeItem('ig-profile');
    activeProfile = 'alex';
  }
}
const pasteKey = (id: string, profile: string) => `ig-pastes:${profile}:${id}`;
function pasteQueue(id: string, profile = activeProfile) {
  const key = pasteKey(id, profile);
  if (!pasteQueues.has(key)) {
    let saved: PasteObservation[] = [];
    try {
      const value: unknown = JSON.parse(localStorage.getItem(key) || '[]');
      if (Array.isArray(value)) saved = value;
    } catch {
      /* Keep new observations in memory if device storage is unavailable. */
    }
    pasteQueues.set(key, saved);
  }
  return pasteQueues.get(key)!;
}
export const pendingPastes = (id: string) => pasteQueue(id);
const pasteChanged = () => window.dispatchEvent(new Event('ig-paste-sync'));
export function queuePaste(id: string, observation: PasteObservation) {
  const queue = pasteQueue(id);
  queue.push({ ...observation, observedAt: new Date().toISOString() });
  try {
    localStorage.setItem(pasteKey(id, activeProfile), JSON.stringify(queue));
  } finally {
    pasteChanged();
  }
}
export function flushPastes(id: string, profile = activeProfile): Promise<unknown> {
  const key = pasteKey(id, profile);
  const running = pasteSyncs.get(key);
  if (running) return running;
  const operation = (async () => {
    let saved: unknown;
    const queue = pasteQueue(id, profile);
    while (queue.length) {
      saved = await (
        await rawRequest(
          `/sessions/${id}/paste`,
          {
            method: 'POST',
            body: JSON.stringify(queue[0]),
          },
          profile,
        )
      ).json();
      queue.shift();
      if (queue.length) localStorage.setItem(key, JSON.stringify(queue));
      else localStorage.removeItem(key);
      pasteChanged();
    }
    return saved;
  })();
  const result = operation.finally(() => pasteSyncs.delete(key));
  pasteSyncs.set(key, result);
  return result;
}
export async function request(path: string, init: RequestInit = {}) {
  const profile = activeProfile;
  const session = path.match(/^\/sessions\/([^/?]+)(?:\/|\?|$)/)?.[1];
  if (session && !['GET', 'HEAD'].includes(init.method ?? 'GET') && !path.endsWith('/paste')) await flushPastes(session, profile);
  return rawRequest(path, init, profile);
}
async function rawRequest(path: string, init: RequestInit = {}, profile = activeProfile) {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: {
        'X-Marvin-Request': '1',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        'X-Pilot-Profile': profile,
        ...(init.body && typeof init.body === 'string'
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(
      'You appear to be offline. Your local text is kept here. Reconnect and try again.',
      0,
    );
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    if (payload.code === 'legal_acceptance_required') window.dispatchEvent(new Event('marvin-terms-required'));
    throw new ApiError(
      payload.error || 'The request could not be completed. Please try again.',
      response.status,
      payload.code,
    );
  }
  return response;
}
export async function api<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  if (accountTransport) return (await accountTransport(path, method, body)) as T;
  return (
    await request(path, {
      method,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  ).json();
}
export async function downloadRecord(id: string, format: 'json' | 'markdown' = 'markdown') {
  const response = await request(`/sessions/${id}/record?format=${format}`);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = `learning-record-${id}.${format === 'json' ? 'json' : 'md'}`;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const words = (text: string) => (text.trim() ? text.trim().split(/\s+/).length : 0);
export const date = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
      }).format(new Date(value))
    : '—';
export const time = (value: string) =>
  new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
export const statusLabel = (status: string) =>
  ({
    baseline: 'First thoughts',
    learning: 'In progress',
    check: 'Independent check',
    complete: 'Completed',
    revisit: 'Time to revisit',
  })[status] || 'Ready to explore';
